import axios from "axios";
import { useEffect, useState, useCallback, useRef } from "react";
import { MapContainer, TileLayer, LayersControl, useMapEvents } from "react-leaflet";
import { Marker, Popup } from "react-leaflet";
import { Modal, Button, Group, Text, Stack, Badge, Paper, TextInput, Select } from "@mantine/core";
import { IconChevronLeft, IconChevronRight, IconSearch } from "@tabler/icons-react";
import useEmblaCarousel from "embla-carousel-react";
import "leaflet/dist/leaflet.css";
import "react-leaflet-markercluster/styles";
import L from "leaflet";
import MarkerClusterGroup from "react-leaflet-markercluster";

// ─── Leaflet icon defaults (module-level, runs once) ──────────────────────────
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
    iconUrl:       "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
    shadowUrl:     "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// Pre-built icon instances — created once at module level, never inside a render
const ICONS = {
    "SEE Learning": new L.Icon({
        iconUrl:   "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png",
        shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png",
        iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
    }),
    CBCT: new L.Icon({
        iconUrl:   "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-orange.png",
        shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png",
        iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
    }),
    Both: new L.Icon({
        iconUrl:   "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png",
        shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png",
        iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
    }),
};
const getMarkerIcon = (trainingType) => ICONS[trainingType] ?? ICONS["SEE Learning"];

// ─── Sub-components defined OUTSIDE Map — stable identity, never remounted on filter change ───

const PhotoCarousel = ({ photos, poiId }) => {
    const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
    const [currentIndex, setCurrentIndex] = useState(0);

    const scroll = (direction) => {
        if (!emblaApi) return;
        if (direction === "prev") {
            emblaApi.scrollPrev();
            setCurrentIndex((p) => (p - 1 + photos.length) % photos.length);
        } else {
            emblaApi.scrollNext();
            setCurrentIndex((p) => (p + 1) % photos.length);
        }
    };

    if (!photos || photos.length === 0)
        return <Text size="sm" color="dimmed">No photos available</Text>;

    return (
        <Stack spacing="xs" style={{ maxWidth: "300px" }}>
            <div style={{ position: "relative", overflow: "hidden", borderRadius: "4px" }}>
                <div ref={emblaRef}>
                    <div style={{ display: "flex" }}>
                        {photos.map((photoPath, i) => (
                            <div key={i} style={{ flex: "0 0 100%", minWidth: 0, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#f0f0f0", height: "200px" }}>
                                <img
                                    src={`https://mindfulafrican.org/poi_server/${photoPath}`}
                                    alt={`POI ${poiId} picha ${i + 1}`}
                                    style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain" }}
                                />
                            </div>
                        ))}
                    </div>
                </div>
                <Badge size="sm" style={{ position: "absolute", bottom: "8px", right: "8px", zIndex: 5 }}>
                    {currentIndex + 1}/{photos.length}
                </Badge>
            </div>
            {photos.length > 1 && (
                <Group spacing="xs" grow>
                    <Button size="xs" variant="light" leftIcon={<IconChevronLeft size={14} />} onClick={() => scroll("prev")}>Prev</Button>
                    <Button size="xs" variant="light" rightIcon={<IconChevronRight size={14} />} onClick={() => scroll("next")}>Next</Button>
                </Group>
            )}
        </Stack>
    );
};

const FullDetailsModal = ({ poi }) => {
    const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
    const [currentIndex, setCurrentIndex] = useState(0);

    const scroll = (direction) => {
        if (!emblaApi) return;
        if (direction === "prev") {
            emblaApi.scrollPrev();
            setCurrentIndex((p) => (p - 1 + poi.photos.length) % poi.photos.length);
        } else {
            emblaApi.scrollNext();
            setCurrentIndex((p) => (p + 1) % poi.photos.length);
        }
    };

    return (
        <div style={{ display: "flex", height: "100%", gap: "20px", padding: "24px" }}>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                {poi.photos?.length > 0 ? (
                    <>
                        <div style={{ position: "relative", overflow: "hidden", borderRadius: "8px", marginBottom: "12px" }}>
                            <div ref={emblaRef}>
                                <div style={{ display: "flex" }}>
                                    {poi.photos.map((photoPath, i) => (
                                        <div key={i} style={{ flex: "0 0 100%", minWidth: 0, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#f0f0f0", height: "500px", position: "relative" }}>
                                            <img
                                                src={`https://mindfulafrican.org/poi_server/${photoPath}`}
                                                alt={`POI ${poi.id} picha ${i + 1}`}
                                                style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain" }}
                                            />
                                            <Badge size="md" style={{ position: "absolute", bottom: "16px", right: "16px", zIndex: 5 }}>
                                                {currentIndex + 1}/{poi.photos.length}
                                            </Badge>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        {poi.photos.length > 1 && (
                            <Group spacing="md" grow>
                                <Button size="md" variant="light" leftIcon={<IconChevronLeft size={16} />} onClick={() => scroll("prev")}>Previous</Button>
                                <Button size="md" variant="light" rightIcon={<IconChevronRight size={16} />} onClick={() => scroll("next")}>Next</Button>
                            </Group>
                        )}
                    </>
                ) : (
                    <Text size="lg" color="dimmed">No photos available</Text>
                )}
            </div>
            <div style={{ flex: 1, overflowY: "auto", paddingRight: "16px" }}>
                <Stack spacing="lg">
                    <div>
                        <Text weight={700} size="xl" mb="xs">{poi.name}</Text>
                        <Group spacing="xs" mb="md">
                            <Badge color={poi.trainingType === "CBCT" ? "orange" : poi.trainingType === "Both" ? "green" : "blue"}>{poi.trainingType}</Badge>
                            <Badge variant="light">{poi.category}</Badge>
                            <Text size="sm" color="dimmed">{new Date(poi.createdDate).toLocaleDateString()}</Text>
                        </Group>
                    </div>
                    <div>
                        <Text weight={600} size="md" mb="xs">Implementation Story</Text>
                        <Text size="md" style={{ lineHeight: 1.6 }}>{poi.story}</Text>
                    </div>
                    {poi.link && (
                        <div>
                            <Text weight={600} size="md" mb="xs">Learn More</Text>
                            <Button component="a" href={poi.link} target="_blank" rel="noopener noreferrer" variant="light" fullWidth>
                                Visit Website/Profile
                            </Button>
                        </div>
                    )}
                    <div style={{ backgroundColor: "#f5f5f5", padding: "16px", borderRadius: "8px" }}>
                        <Text weight={600} size="md" mb="xs">Location</Text>
                        <Text size="sm" mb="xs"><strong>Latitude:</strong> {poi.latitude.toFixed(6)}</Text>
                        <Text size="sm"><strong>Longitude:</strong> {poi.longitude.toFixed(6)}</Text>
                    </div>
                </Stack>
            </div>
        </div>
    );
};

const POIPopupContent = ({ poi, onOpenFullDetails }) => (
    <div style={{ minWidth: "320px", maxWidth: "400px" }}>
        <div style={{ marginBottom: "12px" }}>
            <Text weight={600} size="md" mb="xs">{poi.name}</Text>
            <Group spacing="xs" mb="xs">
                <Badge size="sm" color={poi.trainingType === "CBCT" ? "orange" : poi.trainingType === "Both" ? "green" : "blue"}>{poi.trainingType}</Badge>
                <Badge size="sm" variant="light">{poi.category}</Badge>
                <Text size="xs" color="dimmed">{new Date(poi.createdDate).toLocaleDateString()}</Text>
            </Group>
        </div>
        <div style={{ marginBottom: "12px" }}>
            <PhotoCarousel photos={poi.photos} poiId={poi.id} />
        </div>
        <div style={{ marginBottom: "12px" }}>
            <Text size="sm" weight={500} mb="4px">Implementation Story</Text>
            <Text size="sm" style={{ lineHeight: 1.4 }}>{poi.story}</Text>
        </div>
        <div style={{ backgroundColor: "#f5f5f5", padding: "8px", borderRadius: "4px", marginBottom: "12px" }}>
            <Text size="xs"><strong>Latitude:</strong> {poi.latitude.toFixed(6)}</Text>
            <Text size="xs"><strong>Longitude:</strong> {poi.longitude.toFixed(6)}</Text>
        </div>
        <Button fullWidth color="blue" onClick={() => onOpenFullDetails(poi)}>
            View Full Details
        </Button>
    </div>
);

// Toggles the filter bar visibility directly via a DOM ref — deliberately avoids
// any React state update so the map never re-renders while a popup is open.
const PopupVisibilityController = ({ filterBarRef }) => {
    useMapEvents({
        popupopen:  () => { if (filterBarRef.current) filterBarRef.current.style.visibility = "hidden"; },
        popupclose: () => { if (filterBarRef.current) filterBarRef.current.style.visibility = "visible"; },
    });
    return null;
};

// ─── Main component ────────────────────────────────────────────────────────────

export default function Map() {
    const [pois,             setPOIs]             = useState([]);
    const [filteredPOIs,     setFilteredPOIs]     = useState([]);
    const [fullDetailsModal, setFullDetailsModal] = useState(null);
    const [filterCategory,   setFilterCategory]   = useState("");
    const [filterTrainingType, setFilterTrainingType] = useState("");
    const [searchQuery,      setSearchQuery]      = useState("");

    // Ref for the filter bar — visibility is toggled via the DOM, not React state,
    // so opening/closing a popup never triggers a re-render of the marker layer.
    const filterBarRef = useRef(null);

    // Stable string key derived from the current filtered set.
    // react-leaflet-markercluster only adds markers — it never removes them when
    // children change. Changing this key forces a full unmount + remount of the
    // cluster group so it always reflects exactly filteredPOIs and nothing more.
    const clusterKey = filteredPOIs.map((p) => p.id).join(",");

    useEffect(() => {
        const fetchPOIs = async () => {
            try {
                const res = await axios.get("https://mindfulafrican.org/poi_server/get_pois.php");
                if (res.status === 200 && res.data.success) {
                    setPOIs(res.data.data || []);
                    setFilteredPOIs(res.data.data || []);
                }
            } catch (err) {
                console.error("Error fetching POIs:", err);
            }
        };
        fetchPOIs();
    }, []);

    useEffect(() => {
        let result = pois;
        if (filterCategory)     result = result.filter((p) => p.category     === filterCategory);
        if (filterTrainingType) result = result.filter((p) => p.trainingType === filterTrainingType);
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            result = result.filter((p) =>
                p.name.toLowerCase().includes(q) || p.story.toLowerCase().includes(q)
            );
        }
        setFilteredPOIs(result);
    }, [pois, filterCategory, filterTrainingType, searchQuery]);

    const handleOpenFullDetails = useCallback((poi) => setFullDetailsModal(poi), []);

    return (
        <>
            {/* Full-screen details modal */}
            <Modal
                opened={fullDetailsModal !== null}
                onClose={() => setFullDetailsModal(null)}
                size="100%"
                fullScreen
                title={fullDetailsModal?.name}
                zIndex={1000000}
            >
                {fullDetailsModal && <FullDetailsModal poi={fullDetailsModal} />}
            </Modal>

            {/* Filter bar — visibility toggled via DOM ref, no React state change on popup events */}
            <div
                ref={filterBarRef}
                style={{
                    position: "fixed",
                    bottom: "24px",
                    left: "24px",
                    zIndex: 997,
                    width: "min(360px, calc(100% - 48px))",
                    maxWidth: "360px",
                }}
            >
                <Paper style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.15)", borderRadius: "8px", padding: "16px" }}>
                    <Stack spacing="sm">
                        <TextInput
                            placeholder="Search by name or implementation story..."
                            icon={<IconSearch size={16} />}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.currentTarget.value)}
                        />
                        <Group grow>
                            <Select
                                placeholder="Filter by Category"
                                data={[
                                    { value: "", label: "All Categories" },
                                    { value: "School", label: "School" },
                                    { value: "Organization", label: "Organization" },
                                    { value: "Individual", label: "Individual" },
                                ]}
                                value={filterCategory}
                                onChange={(v) => setFilterCategory(v || "")}
                            />
                            <Select
                                placeholder="Filter by Program"
                                data={[
                                    { value: "", label: "All Programs" },
                                    { value: "SEE Learning", label: "SEE Learning" },
                                    { value: "CBCT", label: "CBCT" },
                                    { value: "Both", label: "Both" },
                                ]}
                                value={filterTrainingType}
                                onChange={(v) => setFilterTrainingType(v || "")}
                            />
                        </Group>
                        <Text size="xs" color="dimmed">
                            Showing {filteredPOIs.length} of {pois.length} locations
                        </Text>
                    </Stack>
                </Paper>
            </div>

            {/* Legend */}
            <Paper style={{ position: "fixed", bottom: "100px", right: "24px", zIndex: 997, boxShadow: "0 4px 12px rgba(0,0,0,0.15)", borderRadius: "8px", padding: "16px", backgroundColor: "white" }}>
                <Stack spacing="sm">
                    <Text weight={600} size="sm">Legend</Text>
                    {[["#1A7FDB", "SEE Learning"], ["#faaf29", "CBCT"], ["#51cf66", "Both"]].map(([color, label]) => (
                        <Group key={label} spacing="xs">
                            <div style={{ width: 12, height: 12, backgroundColor: color, borderRadius: 2 }} />
                            <Text size="xs">{label}</Text>
                        </Group>
                    ))}
                </Stack>
            </Paper>

            {/* Map */}
            <MapContainer
                attributionControl={false}
                center={[15.375592, 36.749879]}
                zoom={2}
                style={{ height: "100vh", width: "100%", backgroundColor: "white" }}
            >
                {/* Controls filter bar visibility via DOM — zero React state mutations */}
                <PopupVisibilityController filterBarRef={filterBarRef} />

                <LayersControl position="bottomright">
                    <LayersControl.BaseLayer checked name="Road Map">
                        <TileLayer url="http://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}" subdomains={["mt0","mt1","mt2","mt3"]} maxZoom={20} />
                    </LayersControl.BaseLayer>
                    <LayersControl.BaseLayer name="Satellite">
                        <TileLayer url="http://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}" subdomains={["mt0","mt1","mt2","mt3"]} maxZoom={20} />
                    </LayersControl.BaseLayer>
                    <LayersControl.BaseLayer name="Terrain">
                        <TileLayer url="http://{s}.google.com/vt/lyrs=p&x={x}&y={y}&z={z}" subdomains={["mt0","mt1","mt2","mt3"]} maxZoom={20} />
                    </LayersControl.BaseLayer>
                    <LayersControl.BaseLayer name="OpenStreetMap">
                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" subdomains={["a","b","c"]} maxZoom={19} />
                    </LayersControl.BaseLayer>
                </LayersControl>

                {/*
                  * key={clusterKey} — the fix for marker accumulation.
                  *
                  * react-leaflet-markercluster passes children to the underlying Leaflet
                  * layer imperatively. When React reconciles a shorter children array,
                  * the removed markers are NOT removed from the Leaflet layer — they
                  * accumulate. Changing the key forces React to fully unmount and remount
                  * the cluster group, so the Leaflet layer is rebuilt from scratch with
                  * exactly the markers in filteredPOIs.
                */}
                <MarkerClusterGroup key={clusterKey}>
                    {filteredPOIs.map((poi) => (
                        <Marker
                            key={poi.id}
                            position={[poi.latitude, poi.longitude]}
                            icon={getMarkerIcon(poi.trainingType)}
                        >
                            <Popup maxWidth={450}>
                                <POIPopupContent poi={poi} onOpenFullDetails={handleOpenFullDetails} />
                            </Popup>
                        </Marker>
                    ))}
                </MarkerClusterGroup>
            </MapContainer>
        </>
    );
}